import React, { useState } from "react";
import { CaretDown, Info, Warning } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import { getEquivalenceTerms } from "../shared";

// Iter 20.21 — Hoisted Markdown-Components-Map (Custom-Renderer für Links,
// damit sie in einem neuen Tab öffnen). Aus Component-Body herausgezogen,
// damit React nicht bei jedem Render einen neuen Component-Type erzeugt
// (Regression, siehe `react/no-unstable-nested-components`).
const ZUSATZ_MD_COMPONENTS = {
  a: ({ node: _node, ...props }) => (
    <a {...props} target="_blank" rel="noopener noreferrer" />
  ),
};

/**
 * Iter 20.03.P5-fix Runde 4/5 — Konsolidierter KV-Eigenheiten-Banner.
 *
 * Fasst alle KV-spezifischen Prüf-/Anwendungshinweise für Schritt 3 in
 * EINEM Aufklärungsblock zusammen — sowohl den bisherigen
 * „Gleichwertigkeitsprüfung erforderlich"-Amber-Banner als auch die
 * neuen Sonderregeln-Erklärungen. Ohne diesen konsolidierten Ansatz
 * hätte der Sachbearbeiter zwei überlappende Warnbanner nebeneinander
 * gesehen, was verwirrend war.
 *
 * Struktur:
 *   ┌── Amber-Section (nur wenn KV Gleichwertigkeit verlangt UND mind.
 *   │   eine relevante Zeile existiert)
 *   └── Sky-Section (nur wenn KV mind. eine aktive Sonderregel hat)
 *
 * Wenn keine Section relevant ist → Component returned null.
 */

/**
 * Detail-Renderer pro Sonderregel-Typ. Text ist bewusst
 * HR-freundlich und listet die konkreten KV-Parameter, damit der
 * Sachbearbeiter weiß, WORAUF er beim Aktivieren im Detail-Bereich zu
 * achten hat (max. Lückendauer, Faktor, max. Anrechnungsjahre …).
 * Spiegelt die Templates aus `CapBypassHints.jsx` — DIY-Duplikate
 * bewusst in Kauf genommen, damit der Banner ohne Kontext-Zugriff auf
 * den Row-Zustand rendert.
 */
const RULE_RENDERERS = {
  selber_arbeitgeber_lueckenregel: (rule) => {
    const monate = Number(rule.max_lueckendauer_monate) || 0;
    return {
      title: "Lückenregel \u201eSelber Arbeitgeber\u201c",
      body: monate > 0 ? (
        <>
          Vordienstzeiten beim aktuellen Arbeitgeber zählen zusätzlich zur
          Höchstanrechnungsdauer, wenn die zeitliche Lücke zwischen der
          Vordienstzeit und der aktuellen Anstellung höchstens{" "}
          <strong className="font-semibold">{monate} Monate</strong>{" "}
          beträgt. Bitte pro Zeile prüfen.
        </>
      ) : (
        <>
          Vordienstzeiten beim aktuellen Arbeitgeber zählen zusätzlich zur
          Höchstanrechnungsdauer, wenn die zeitliche Lücke gering ist —
          bitte pro Zeile prüfen.
        </>
      ),
    };
  },
  selber_arbeitgeber_vorarbeiterzeit: (rule) => {
    const faktor = Number(rule.faktor);
    const maxJahre = Number(rule.max_jahre) || 0;
    const prozent =
      Number.isFinite(faktor) && faktor > 0 ? Math.round(faktor * 100) : null;
    return {
      title: "Vorarbeiterzeit \u201eSelber Arbeitgeber\u201c",
      body: (
        <>
          Vorarbeiterzeiten beim aktuellen Arbeitgeber werden
          {prozent !== null && (
            <>
              {" "}mit <strong className="font-semibold">{prozent} %</strong>
            </>
          )}{" "}
          angerechnet
          {maxJahre > 0 && (
            <>
              , max.{" "}
              <strong className="font-semibold">{maxJahre} Jahre</strong> in
              Summe
            </>
          )}
          . Bitte pro Zeile berücksichtigen.
        </>
      ),
    };
  },
  selber_arbeitgeber_gleiche_gruppe: () => ({
    title: "Gleiche/höhere Verwendungsgruppe",
    body: (
      <>
        Vordienstzeiten beim aktuellen Arbeitgeber zählen zusätzlich zur
        Höchstanrechnungsdauer, wenn die Vordienstposition der{" "}
        <strong className="font-semibold">gleichen oder einer höheren
        Verwendungsgruppe</strong> entsprach. Bitte pro Zeile eruieren.
      </>
    ),
  }),
  // Iter 20.12 — Zeitfenster-Regel: Anrechnung nur für die letzten N Jahre
  // vor dem Stichtag. Wird automatisch angewendet (kein User-Toggle nötig),
  // Hinweis dient nur der Transparenz.
  zeitfenster_relevanz: (rule) => {
    const jahre = Number(rule.zeitfenster_jahre) || 0;
    if (jahre <= 0) return null;
    return {
      title: "Anrechnungsfenster",
      body: (
        <>
          Nur Vordienstzeiten der{" "}
          <strong className="font-semibold">letzten {jahre} Jahre</strong>{" "}
          vor dem Stichtag werden angerechnet. Ältere Zeiträume werden
          automatisch ausgeschlossen; teilweise ins Fenster reichende
          Beschäftigungen werden am Fenster-Beginn abgeschnitten.
        </>
      ),
    };
  },
  // Iter 20.36.f2 — Globaler Hinweis für die WT-Kanzlei-Kombiregel.
  // Iter 20.36.f5 — Text an die neuen Feldnamen + Zwei-Töpfe-Semantik
  // angepasst (Faktor-Halbierung + Sub-Cap für Non-WT-Zeilen).
  wt_kanzlei_kombiregel: (rule) => {
    const maxJahreWT = Number(rule.max_jahre_wt) || 0;
    const maxJahreNonWT = Number(rule.max_jahre_non_wt) || 0;
    const faktorNonWT = Number(rule.faktor_non_wt);
    const prozent =
      Number.isFinite(faktorNonWT) && faktorNonWT > 0
        ? Math.round(faktorNonWT * 100)
        : null;
    return {
      title: "WT-Kanzlei-Kombiregel",
      body: (
        <>
          Vordienstzeiten in einer{" "}
          <strong className="font-semibold">Wirtschaftstreuhand-Kanzlei</strong>{" "}
          werden voll angerechnet
          {maxJahreWT > 0 && (
            <>
              {" "}
              bis zur Gesamt-Höchstanrechnungsdauer von{" "}
              <strong className="font-semibold">{maxJahreWT} Jahren</strong>
            </>
          )}
          . Vordienstzeiten außerhalb einer WT-Kanzlei zählen
          {prozent !== null && (
            <>
              {" "}
              nur mit{" "}
              <strong className="font-semibold">{prozent} %</strong>
            </>
          )}
          {maxJahreNonWT > 0 && (
            <>
              {" "}
              und max. bis zu{" "}
              <strong className="font-semibold">{maxJahreNonWT} Jahren</strong>{" "}
              effektiv anrechenbar
            </>
          )}
          . Bitte pro Zeile die Checkbox &bdquo;In WT-Kanzlei erbracht&ldquo; setzen.
        </>
      ),
    };
  },
  // Iter 20.36.f3 — Altersfaktor: zeitwertige Anrechnung. Regel läuft
  // automatisch in der Math-Engine (keine User-Aktion pro Zeile nötig).
  // Der Sachbearbeiter erkennt reduzierte Zeilen an der ergänzten
  // Begründung („Altersfaktor: …") und am `_altersfaktor_applied`-Badge.
  //
  // Iter 20.36.f14.11 — Text auf den KV-Originaltext geändert, damit die
  // Formulierung 1:1 dem juristischen Wortlaut entspricht (facheinschlägige
  // Tätigkeiten, WT-Kanzlei-Ausnahme).
  facheinschlaegig_altersfaktor_regel: (rule) => {
    const zf = Number(rule.zeitfenster_jahre) || 0;
    const maxJ = Number(rule.max_jahre) || 0;
    const faktor = Number(rule.faktor);
    if (zf <= 0 || !Number.isFinite(faktor) || faktor <= 0) return null;
    return {
      title: "Altersfaktor · zeitwertige Anrechnung",
      body: (
        <>
          <strong className="font-semibold">Facheinschlägige Tätigkeiten</strong>
          , die vor mehr als{" "}
          <strong className="font-semibold">{zf} Jahren</strong> erbracht
          wurden, sind nur{" "}
          <strong className="font-semibold">zur Hälfte</strong>{" "}
          anzurechnen, wenn in den letzten{" "}
          <strong className="font-semibold">{zf} Jahren</strong> vor
          Beginn des Dienstverhältnisses nicht mindestens{" "}
          <strong className="font-semibold">
            {maxJ > 0 ? `${maxJ} Jahre` : "fünf Jahre"}
          </strong>{" "}
          facheinschlägig gearbeitet wurde. Weiters gilt auch hier die
          Regelung der WT-Kanzlei.
        </>
      ),
    };
  },
  // Iter 20.36.f14.12 — Präsenzdienst/Zivildienst-Regel. Modus steuert,
  // ob die gesetzliche Umschließungs-Prüfung durch das Dienstverhältnis
  // an den Sachbearbeiter delegiert wird (`gesetzlich`, Default) oder ob
  // Präsenz-/Zivildienst-Zeiten KV-seitig immer voll anrechenbar sind
  // (`immer_anrechenbar`, z.B. KV Handel Angestellte).
  praesenzdienst_regel: (rule) => {
    const modus = String(rule.praesenzdienst_modus || "gesetzlich").toLowerCase();
    if (modus === "immer_anrechenbar") {
      return {
        title: "Präsenz-/Zivildienst · immer anrechenbar",
        body: (
          <>
            <strong className="font-semibold">Präsenz- und Zivildienstzeiten</strong>{" "}
            werden laut diesem KV{" "}
            <strong className="font-semibold">immer voll angerechnet</strong>{" "}
            — unabhängig davon, ob sie durch ein Dienst- oder Lehrverhältnis
            umschlossen sind.
          </>
        ),
      };
    }
    return {
      title: "Präsenz-/Zivildienst · gesetzliche Prüfung",
      body: (
        <>
          Diese Zeilen sind laut KV{" "}
          <strong className="font-semibold">voll anrechenbar</strong>, sie
          gelten jedoch nur dann, wenn sie{" "}
          <strong className="font-semibold">
            durch ein Dienst- oder Lehrverhältnis umschlossen
          </strong>{" "}
          sind. Bitte prüfen und ggf. &bdquo;Anrechnen&ldquo; deaktivieren.
        </>
      ),
    };
  },
};

export default function KvHinweisBanner({
  equivalenceRequired,
  hasEquivalenceRowsToReview,
  sonderregeln,
  zusatzHinweis,
  equivalenceTerminology,
}) {
  const showEquivalence = equivalenceRequired === true && hasEquivalenceRowsToReview === true;
  const terms = getEquivalenceTerms(equivalenceTerminology);

  const activeSonderregeln = Array.isArray(sonderregeln)
    ? sonderregeln.filter((r) => r && r.aktiv !== false)
    : [];
  const showSonderregeln = activeSonderregeln.length > 0;

  const trimmedZusatz = typeof zusatzHinweis === "string" ? zusatzHinweis.trim() : "";
  const showZusatzHinweis = trimmedZusatz.length > 0;

  // Iter 20.36.f14.19 — KV-Sonderregeln + Zusatzhinweis initial collapsed.
  // Der Facheinschlägigkeits-Banner (`showEquivalence`) bleibt IMMER expanded
  // — er ist die eigentliche Handlungsaufforderung. Die beiden anderen
  // Blöcke sind rein informational und können nach Bedarf aufgeklappt
  // werden. Kein State-Persistenz nötig — Kollaps-Zustand ist pro
  // Component-Instanz (User erneut auf Step 3 → wieder collapsed).
  const [sonderregelnOpen, setSonderregelnOpen] = useState(false);
  const [zusatzOpen, setZusatzOpen] = useState(false);

  if (!showEquivalence && !showSonderregeln && !showZusatzHinweis) return null;

  return (
    <div
      data-testid="tool-step3-kv-hinweis-banner"
      className="mb-3 rounded-sm border border-warm-200 bg-warm-50/60 overflow-hidden"
    >
      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest font-semibold text-warm-600 border-b border-warm-200/70">
        KV-Infos
      </div>

      {showEquivalence && (
        <div
          data-testid="tool-step3-kv-hinweis-equivalence"
          className="flex items-start gap-2.5 border-l-4 border-amber-400 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900 leading-snug"
        >
          <Warning
            size={16}
            weight="fill"
            className="shrink-0 mt-0.5 text-amber-600"
          />
          <div>
            <div className="font-semibold mb-0.5">
              {terms.pruefung} erforderlich
            </div>
            Dieser KV verlangt eine Prüfung, ob die unten markierten{" "}
            <strong>Positionen</strong> {terms.qualified_adj} zur neuen
            Tätigkeit sind. Bitte jede gelb hinterlegte Zeile einzeln
            prüfen — falls <strong>nicht {terms.adj}</strong>, das
            Häkchen bei &bdquo;Anrechnen&ldquo; entfernen.
          </div>
        </div>
      )}

      {showSonderregeln && (
        <div
          data-testid="tool-step3-kv-hinweis-sonderregeln"
          className="flex items-start gap-2.5 border-l-4 border-sky-400 bg-sky-50/70 px-3 py-2.5 text-[12px] text-sky-900 leading-snug"
        >
          <Info
            size={16}
            weight="fill"
            className="shrink-0 mt-0.5 text-sky-600"
          />
          <div className="min-w-0 flex-1">
            {/* Iter 20.36.f14.19 — Header ist jetzt eine Toggle-Zeile.
                Klick auf die Überschrift (oder den Chevron) klappt den
                Details-Block ein/aus. Collapsed-Zustand zeigt die Liste
                der aktiven Regel-Titel als Sub-Line, damit der User auf
                einen Blick weiß, WELCHE Sonderregeln greifen — ohne
                den ganzen Erklärungstext zu sehen. */}
            <button
              type="button"
              onClick={() => setSonderregelnOpen((v) => !v)}
              data-testid="tool-step3-kv-hinweis-sonderregeln-toggle"
              aria-expanded={sonderregelnOpen}
              className="group flex items-center gap-1.5 w-full text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-500 rounded-sm"
            >
              <span className="font-semibold text-sky-900 flex-1">
                KV-Sonderregeln für die Vordienstzeit-Anrechnung
              </span>
              <CaretDown
                size={12}
                weight="bold"
                className={`shrink-0 text-sky-700 transition-transform ${
                  sonderregelnOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
            {!sonderregelnOpen && (
              <div
                className="mt-0.5 text-[11px] text-sky-800/85 leading-snug"
                data-testid="tool-step3-kv-hinweis-sonderregeln-summary"
              >
                {activeSonderregeln
                  .map((rule) => {
                    const renderer = RULE_RENDERERS[rule.typ];
                    if (!renderer) return null;
                    const { title } = renderer(rule) || {};
                    return title || null;
                  })
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            )}
            {sonderregelnOpen && (
              <>
                <ul className="space-y-1.5 mb-2 mt-1.5">
                  {activeSonderregeln.map((rule, i) => {
                    const renderer = RULE_RENDERERS[rule.typ];
                    if (!renderer) return null;
                    const { title, body } = renderer(rule);
                    if (!body) return null;
                    return (
                      <li
                        key={`${rule.typ}-${i}`}
                        data-testid={`tool-step3-kv-hinweis-sonderregel-${rule.typ}`}
                        className="pl-3 border-l-2 border-sky-200"
                      >
                        <div className="font-semibold text-sky-800">{title}</div>
                        <div className="text-sky-900/95">{body}</div>
                      </li>
                    );
                  })}
                </ul>
                <div className="text-[11.5px] text-sky-800/90 border-t border-sky-200 pt-1.5">
                  {(() => {
                    // Iter 20.12 — Regel-Klassifikation für den Footer-Text.
                    // Iter 20.36.f3 — „facheinschlaegig_altersfaktor_regel"
                    // ist ebenfalls automatisch (deterministische
                    // Faktor-Reduktion in der Math-Engine, keine User-Aktion).
                    const AUTO_TYPES = new Set([
                      "zeitfenster_relevanz",
                      "facheinschlaegig_altersfaktor_regel",
                      "praesenzdienst_regel",
                    ]);
                    const hasManual = activeSonderregeln.some(
                      (r) => !AUTO_TYPES.has(r.typ)
                    );
                    const hasAuto = activeSonderregeln.some(
                      (r) => AUTO_TYPES.has(r.typ)
                    );
                    if (hasManual && hasAuto) {
                      return (
                        <>
                          Automatische Regeln greifen ohne Zutun — die übrigen
                          Regeln bitte pro Vordienstzeit im Detail-Bereich
                          aktivieren oder deaktivieren.
                        </>
                      );
                    }
                    if (hasManual) {
                      return (
                        <>
                          Diese Regeln greifen{" "}
                          <strong className="font-semibold">nicht automatisch</strong>{" "}
                          — bitte pro Vordienstzeit im Detail-Bereich aktivieren
                          oder deaktivieren, damit die Anrechnung korrekt
                          berechnet wird.
                        </>
                      );
                    }
                    return (
                      <>
                        Diese Regeln greifen{" "}
                        <strong className="font-semibold">automatisch</strong> —
                        entsprechende Vordienstzeiten werden bereits im Ergebnis
                        berücksichtigt.
                      </>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Iter 20.21 — Admin-editierbarer Zusatzhinweis (markdown-fähig).
          Reine Info, keine Logik-Auswertung. Angezeigt als eigener Sky-
          Block unter den Sonderregeln — visuell abgehoben durch ein
          eigenes „Zusatzhinweis"-Label.
          Iter 20.36.f14.19 — Ebenfalls einklappbar, initial collapsed. */}
      {showZusatzHinweis && (
        <div
          data-testid="tool-step3-kv-hinweis-zusatz"
          className="flex items-start gap-2.5 border-l-4 border-sky-300 bg-sky-50/50 px-3 py-2.5 text-[12px] text-sky-900 leading-snug"
        >
          <Info
            size={16}
            weight="regular"
            className="shrink-0 mt-0.5 text-sky-500"
          />
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setZusatzOpen((v) => !v)}
              data-testid="tool-step3-kv-hinweis-zusatz-toggle"
              aria-expanded={zusatzOpen}
              className="group flex items-center gap-1.5 w-full text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-500 rounded-sm"
            >
              <span className="font-semibold text-sky-900 flex-1">
                Zusatzhinweis zum Kollektivvertrag
              </span>
              <CaretDown
                size={12}
                weight="bold"
                className={`shrink-0 text-sky-700 transition-transform ${
                  zusatzOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </button>
            {zusatzOpen && (
              <div
                className="mt-1.5 prose prose-sm max-w-none text-sky-900/95
                           [&>p]:mb-1.5 [&>p:last-child]:mb-0
                           [&>ul]:list-disc [&>ul]:pl-4 [&>ul]:mb-1.5
                           [&>ol]:list-decimal [&>ol]:pl-4 [&>ol]:mb-1.5
                           [&>ul>li]:mb-0.5 [&>ol>li]:mb-0.5
                           [&_strong]:font-semibold
                           [&_a]:text-sky-700 [&_a]:underline"
              >
                <ReactMarkdown components={ZUSATZ_MD_COMPONENTS}>
                  {trimmedZusatz}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
